import axios from 'axios'
import sha256 from 'sha256'
import xbb from '@xbb/xbb-utils'
import utils, { isMobile } from '../utils/index.js'
import getVNodeMsg from '@/utils/msg-error-vnode.js'
import { getPlatformUrl } from '../config/config.js'
import xbbError from '@xbb/xbb-error'
import SlsTracker from '@aliyun-sls/web-track-browser'
import { ElMessage } from 'element-plus'
import xbbSdk from 'xbb-sdk'
import { useNveasyBluetoothStore } from '@/store/useNveasyBluetoothStore'

const {
  WEBPACK_GATEWAY_URL,
  WEBPACK_NEW_GATEWAY_URL,
  WEBPACK_NEW_GATEWAY_URL_INDEPENDENT,
  WEBPACK_ENV,
  WEBPACK_IS_PRODUCTON
} = process.env

// AI 网关路由表：prefix -> [常规网关, 独立部署网关]
const AI_GATEWAY_ROUTES = [
  {
    prefix: '/api/qca',
    urls: [WEBPACK_NEW_GATEWAY_URL, WEBPACK_NEW_GATEWAY_URL_INDEPENDENT]
  }
]

/**
 * 兜底回填：ai_xxx 是 xxx 的备份字段，主字段缺失时用备份赋值，并返回最新值
 * @param key 主字段名
 * @returns 当前值
 */
function ensureFromBackup(key) {
  const backupKey = `ai_${key}`
  if (!utils.xbbLocalStorageGet(key) && utils.xbbLocalStorageGet(backupKey)) {
    utils.xbbLocalStorageSet(key, utils.xbbLocalStorageGet(backupKey))
  }
  return utils.xbbLocalStorageGet(key)
}

// 是否走独立部署（私有化）网关
const isIndependentGateway = () =>
  !xbb.isDevEnv() && utils.getPlatform(ensureFromBackup('corpid')) !== 'dd'

// 是否命中 AI 网关路由
const isAiGatewayUrl = (url = '') => AI_GATEWAY_ROUTES.some((r) => url.startsWith(r.prefix))

const DURATION_3000 = 3000 // 消息组件默认展示时长3000ms
const DURATION_5000 = 5000 // 消息组件特殊展示时长5000ms

// 全局的token刷新状态控制
let refreshTokenPromise = null
const _Message = function (params = {}) {
  ElMessage({
    duration: DURATION_3000,
    showClose: true,
    ...params
  })
}

// sls接口请求日志
const opts = {
  host: 'cn-zhangjiakou.log.aliyuncs.com', // 所在地域的服务入口。例如cn-hangzhou.log.aliyuncs.com
  project: 'xbb-production-logs', // Project名称。
  logstore: 'xbbprofront', // Logstore名称。
  time: 5, // 发送日志的时间间隔，默认是10秒。
  count: 10, // 发送日志的数量大小，默认是10条。
  topic: 'request', // 自定义日志主题。
  source: 'web-request',
  tags: {
    platform: isMobile() ? 'mobile' : 'web'
  }
}

const tracker = new SlsTracker(opts)

// 生成请求ID
function createRequestId() {
  return xbb.getGuid().replace(/-/g, '').substring(0, 16)
}

/**
 * 清空登录信息并刷新页面
 */
function reload() {
  utils.xbbLocalStorageRemove('corpid')
  utils.xbbLocalStorageRemove('userId')
  utils.xbbLocalStorageRemove('xbbAccessToken')
  utils.xbbLocalStorageRemove('xbbRefreshToken')
  utils.xbbLocalStorageRemove('ai_corpid')
  utils.xbbLocalStorageRemove('ai_userId')
  utils.xbbLocalStorageRemove('ai_xbbAccessToken')
  useNveasyBluetoothStore.resetBluetoothSession().catch(() => {})
  if (window.plus) {
    // 获取当前窗口并刷新
    window.plus.webview.currentWebview().reload()
  } else {
    // 非 H5+ App 环境，使用传统方法
    window.location.reload()
  }
}

/**
 * 将 data 统一转为对象，便于统一序列化与签名
 */
function ensureObjectData(data) {
  if (data == null) return {}
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }
  return typeof data === 'object' ? data : {}
}

/**
 * 重新获取token - 使用原生请求方式避免循环依赖
 */
async function refetchToken(http, originalConfig, msg) {
  // 以 refreshTokenPromise 是否存在为“单一真相”，避免并发窗口
  if (refreshTokenPromise) {
    try {
      await refreshTokenPromise
      // 刷新完成后，重发原请求
      if (http && originalConfig) {
        if (originalConfig._retry) {
          throw new Error('重试次数超限')
        }
        originalConfig._retry = true
        if (originalConfig.headers) {
          delete originalConfig.headers.sign
          delete originalConfig.headers['X-Request-ID']
        }
        // 保证 data 为对象，由拦截器统一 stringify + 签名
        originalConfig.data = ensureObjectData(originalConfig.data)
        await new Promise((r) => setTimeout(r, 60))
        return http(originalConfig)
      }
      return Promise.resolve()
    } catch (err) {
      return Promise.reject(err)
    }
  }

  const xbbRefreshToken = utils.xbbLocalStorageGet('xbbRefreshToken')

  if (!xbbRefreshToken) {
    msg && ElMessage.error(msg)
    reload()
    throw new Error('未获取到xbbRefreshToken')
  }

  // 原子设置，尽早占位，避免并发窗口
  refreshTokenPromise = performTokenRefresh(xbbRefreshToken)

  try {
    await refreshTokenPromise
  } catch (err) {
    console.error('刷新token失败', err)
    throw err
  } finally {
    // 统一复位
    refreshTokenPromise = null
  }

  if (http && originalConfig) {
    if (originalConfig._retry) {
      throw new Error('重试次数超限')
    }

    originalConfig._retry = true

    if (originalConfig.headers) {
      delete originalConfig.headers.sign
      delete originalConfig.headers['X-Request-ID']
    }

    // 统一对象化，交由拦截器做序列化与签名
    originalConfig.data = ensureObjectData(originalConfig.data)

    await new Promise((resolve) => setTimeout(resolve, 60))

    return await http(originalConfig)
  }

  return Promise.resolve()
}

/**
 * 执行token刷新的具体逻辑
 */
async function performTokenRefresh(xbbRefreshToken) {
  // 获取平台标识
  const platformFlag = utils.xbbLocalStorageGet('platformFlag')

  // 构建请求URL
  let baseURL = WEBPACK_GATEWAY_URL || ''
  baseURL = getPlatformUrl(platformFlag, WEBPACK_ENV) || WEBPACK_GATEWAY_URL
  if (['development'].includes(WEBPACK_ENV || '')) {
    baseURL = ''
  }

  // 准备请求数据
  const corpid = utils.xbbLocalStorageGet('corpid') || ''
  const userId = utils.xbbLocalStorageGet('userId') || ''
  const requestData = {
    corpid,
    userId,
    platform: isMobile() ? 'dingtalk' : 'web',
    frontDev: xbb.isDevEnv() ? '1' : undefined,
    appTag: platformFlag === 'dingtalk' ? '1' : '2',
    refreshToken: xbbRefreshToken
  }

  const API_PREFIX = '/pro/v1'
  const localLang = utils.xbbLocalStorageGet('VUE_LANG') || 'zh_CN'
  let url = `${API_PREFIX}/inside/ai/chatbot/getRefreshAccessToken?lang=${localLang}`

  // 开发环境处理
  if (WEBPACK_ENV === 'development') {
    url = `/dev${url}`
  }

  // 格式化请求数据并创建签名
  const bodyString = JSON.stringify(requestData)
  const token = ensureFromBackup('xbbAccessToken')
  const sign = sha256(bodyString.trim() + (token || '1'))

  // 使用原生的XMLHttpRequest避免拦截器干扰
  const response = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', baseURL + url)
    xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8')
    xhr.setRequestHeader('sign', sign)
    xhr.setRequestHeader('X-Request-ID', createRequestId())
    xhr.timeout = 30000

    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const responseData = JSON.parse(xhr.responseText)
          resolve({ data: responseData })
        } catch (e) {
          console.error('解析响应JSON失败:', e)
          reject(new Error('解析响应数据失败'))
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`))
      }
    }

    xhr.onerror = function () {
      console.error('---原生请求错误---')
      reject(new Error('网络请求失败'))
    }

    xhr.ontimeout = function () {
      console.error('---原生请求超时---')
      reject(new Error('请求超时'))
    }

    xhr.send(bodyString)
  })

  const { data: responseData } = response

  // 检查响应数据结构
  if (!responseData || typeof responseData !== 'object') {
    console.error('响应数据格式错误', responseData)
    throw new Error('响应数据格式错误')
  }

  const { result } = responseData

  if (result && result.xbbAccessToken && result.xbbRefreshToken) {
    // 替换原 token
    utils.xbbLocalStorageSet('xbbAccessToken', result.xbbAccessToken)
    utils.xbbLocalStorageSet('xbbRefreshToken', result.xbbRefreshToken, 1000 * 60 * 60 * 24 * 30)

    return { data: responseData, ...result }
  } else {
    console.error('token刷新失败：未获取到有效的token', result)
    reload()
    throw new Error('未获取到有效的token')
  }
}

/**
 * 从字符串中提取 JSON 数据并解析
 * @param str 包含 JSON 的字符串
 * @returns 解析后的对象
 */
function extractJsonFromString(str) {
  // 移除字符串开头和结尾的多余引号
  const trimmedStr = str.replace(/^"|"$/g, '')

  // 找到 JSON 数据的开始和结束位置
  const startIndex = trimmedStr.indexOf('{')
  const endIndex = trimmedStr.lastIndexOf('}') + 1

  if (startIndex === -1 || endIndex === -1) {
    throw new Error(str)
  }

  // 提取 JSON 部分
  const jsonStr = trimmedStr.substring(startIndex, endIndex)

  try {
    // 解析 JSON 字符串
    const result = JSON.parse(jsonStr)

    // 如果 chatInfo 字段是字符串，则进一步解析
    if (typeof result.chatInfo === 'string') {
      try {
        result.chatInfo = JSON.parse(result.chatInfo)
      } catch (e) {
        console.warn('Failed to parse chatInfo:', e)
        // 保持原样不处理
      }
    }

    return result
  } catch (e) {
    throw new Error(`Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// 创建 axios 实例的封装类
class HttpRequest {
  /**
   * 发送请求
   * @param options axios 请求配置
   * @returns Promise<any>
   */
  request(options) {
    let baseURL = WEBPACK_GATEWAY_URL || ''
    const aiRoute = AI_GATEWAY_ROUTES.find((r) => options.url.startsWith(r.prefix))

    if (aiRoute) {
      baseURL = aiRoute.urls[isIndependentGateway() ? 1 : 0]
    } else {
      // 获取平台标识，动态设置 baseURL
      const platformFlag = utils.xbbLocalStorageGet('platformFlag')
      baseURL = getPlatformUrl(platformFlag, WEBPACK_ENV) || WEBPACK_GATEWAY_URL // 无平台标识，则拿默认的网关地址
      // 开发环境下不设置 baseURL
      if (['development'].includes(WEBPACK_ENV || '')) {
        baseURL = ''
      }
    }
    // 创建 axios 实例
    const http = axios.create({
      baseURL,
      method: 'post',
      // timeout: 16000,
      headers: {
        'Content-Type': 'application/json;charset=UTF-8'
      }
    })
    // 设置拦截器
    this.interceptors(http, options.aliCallCenter)
    return http(options)
  }

  /**
   * 设置请求和响应拦截器
   * @param http axios 实例
   * @param aliCallCenter 是否阿里云呼叫中心
   */
  interceptors(http, aliCallCenter = false) {
    // 请求拦截器
    http.interceptors.request.use(
      (config) => {
        // 默认参数，自动带上 corpid、userId、平台等信息
        const corpid = ensureFromBackup('corpid')
        const userId = ensureFromBackup('userId')

        const defaultParams = {
          corpid: corpid || '',
          userId: userId || '',
          platform: isMobile() ? 'dingtalk' : 'web',
          frontDev: xbb.isDevEnv() ? '1' : undefined
        }
        if (config.url.startsWith('/api/agents')) {
          defaultParams.instanceKey = utils.xbbLocalStorageGet('instanceKey') || 'sales-mentor'
        }

        // 统一对象化数据，避免因字符串化差异导致签名不一致
        const baseData = ensureObjectData(config.data)
        config.data = Object.assign({}, defaultParams, baseData)

        const requestId = createRequestId()
        // 仅在生产环境且在正式环境发送请求日志
        if (WEBPACK_IS_PRODUCTON) {
          tracker.send({
            ...config.data,
            log_request_url: config.url,
            device_ua: navigator.userAgent,
            page_url: location.href,
            request_id: requestId
          })
        }

        // 处理 multipart/form-data 类型
        if (config.headers['Content-Type'] === 'multipart/form-data') {
          const formData = new FormData()
          for (const item in config.data) {
            const value = config.data[item]
            if (item === 'files' && Array.isArray(value)) {
              value.forEach(({ file, path }) => formData.append(item, file, path))
            } else {
              formData.append(item, value)
            }
          }

          // 对于 multipart 的签名，仍使用 JSON 字符串进行签名（与后端约定一致）
          const bodyStringForSign = this.formatParams(config.data)
          const newSign = this.createSign(bodyStringForSign)
          config.headers.sign = newSign
          // 添加请求id
          config.headers['X-Request-ID'] = requestId
          // 发送体为 FormData
          config.data = formData

          return config
        } else {
          // 其他类型转为字符串
          const bodyString = this.formatParams(config.data)
          // 添加签名字段
          const newSign = this.createSign(bodyString)
          config.headers.sign = newSign
          // 添加请求id
          config.headers['X-Request-ID'] = requestId
          // 最终发送体为字符串
          config.data = bodyString

          return config
        }
      },
      (err) => {
        return Promise.reject(err)
      }
    )

    // 阿里云呼叫中心不需要响应拦截
    if (aliCallCenter) return

    // 响应拦截器
    http.interceptors.response.use(
      async (response) => {
        if (response) {
          const { data, config } = response

          // 如果是刷新token的请求，直接返回，不进行拦截器处理
          if (config.headers && config.headers['X-Refresh-Token-Request'] === 'true') {
            return response
          }

          const { code, msg } = data
          // 登录失效或未授权
          if (code === 100012 || code === 100128) {
            // if (xbb.isDevEnv()) {
            //   // 跳到本地登录页
            //   window.location.href = '/#/devlogin'
            // }
            // 获取完整 URL 中的搜索参数部分（即 "?inxbb=1"）
            const searchParams = new URLSearchParams(window.location.search)
            // 通过参数名 "inxbb" 获取对应的值
            const inxbbValue = searchParams.get('inxbb') || ''
            if (isMobile() && +inxbbValue !== 1) {
              // 统一交由 refetchToken 处理并发与重发，避免在此分支重复判断
              return refetchToken(http, config, msg)
            } else if (code === 100012 && +inxbbValue === 1) {
              // 登录失效  &&  在iframe中，退出登录
              xbbSdk.sendMessage('signout')
            }
            return Promise.reject(data)
          } else if (/\/ai\/chat\/getSseMsg/.test(config?.url) && data) {
            // 特殊接口处理，返回 JSON 对象
            return extractJsonFromString(data)
          } else if (code === 100063 || code === 100064) {
            _Message({
              duration: DURATION_5000,
              message: getVNodeMsg(response?.data?.requestId, msg),
              type: 'error'
            })
          } else if (code !== 1) {
            // 其他错误
            msg &&
              _Message({
                message: msg,
                type: 'error'
              })

            return Promise.reject(data)
          }
          // 正常返回
          return data
        } else {
          return {}
        }
      },
      (err) => {
        console.error(err)
        if (err && err.response) {
          // 捕获错误代码
          xbbError.errorCatch(
            { ...err, message: `${err.response.status}${err.response.config.url}` },
            1
          )
          // 根据状态码设置错误信息
          switch (err.response.status) {
            case 400:
              err.message = '请求错误'
              break
            case 401:
              err.message = '未授权，请登录'
              break
            case 403:
              err.message = '拒绝访问'
              break
            case 404:
              err.message = `请求地址出错:${err.response.config.url}`
              break
            case 408:
              err.message = '请求超时'
              break
            case 500:
              err.message = '服务器正忙，请稍后再试'
              break
            case 501:
              err.message = '服务未实现'
              break
            case 502:
              err.message = '网关错误'
              break
            case 503:
              err.message = '服务不可用'
              break
            case 504:
              err.message = '网管超时'
              break
            case 505:
              err.message = 'HTTP版本不受支持'
              break
            default:
          }
          const noErrorMessage = err.config.noErrorMessage
          // 只在非502/504状态码且没有设置noErrorMessage时显示错误消息
          if (![502, 504].includes(err.response.status) && !noErrorMessage) {
            _Message({
              duration: DURATION_5000,
              message: getVNodeMsg(err.response.config.headers['X-Request-ID'], err.message),
              type: 'error'
            })
          }
        }
        return Promise.reject(err)
      }
    )
  }

  /**
   * 深拷贝对象，防止引用类型数据污染
   * @param source 源对象
   * @returns 新对象
   */
  deepClone(source) {
    // 如果不是对象或者是null，直接返回
    if (!source || typeof source !== 'object') {
      return source
    }
    // 如果是字符串，直接返回（字符串虽然typeof === 'object'在某些情况下，但我们不需要深拷贝）
    if (typeof source === 'string') {
      return source
    }
    const targetObj = source.constructor === Array ? [] : {}
    for (const keys in source) {
      if (!Object.prototype.hasOwnProperty.call(source, keys)) continue
      if (source[keys] && typeof source[keys] === 'object') {
        targetObj[keys] = this.deepClone(source[keys])
      } else if (source[keys] !== undefined && source[keys] !== null) {
        targetObj[keys] = source[keys]
      }
    }
    return targetObj
  }

  /**
   * 创建签名
   * @param bodyString 请求体字符串
   * @returns 签名字符串
   */
  createSign(bodyString) {
    const token = ensureFromBackup('xbbAccessToken')
    return sha256(bodyString.trim() + (token || '1'))
  }

  /**
   * 过滤特殊字符（如控制字符等）
   * @param str 字符串
   * @returns 过滤后的字符串
   */
  CharacterFilter(str) {
    let newStr = ''
    for (let i = 0; i < str.length; i++) {
      const us = str.charCodeAt(i)
      if (!(us < 0x20 || us === 0x7f || us === 0x2028 || us === 0x2029)) {
        newStr += str.charAt(i)
      }
    }
    return newStr
  }

  /**
   * 格式化参数，将对象转为字符串并过滤特殊字符
   * @param data 参数对象
   * @returns 字符串
   */
  formatParams(data) {
    // 如果已经是字符串，直接返回避免重复序列化
    if (typeof data === 'string') {
      return data
    }
    const obj = ensureObjectData(data)
    let bodyString = JSON.stringify(obj)
    if (bodyString.length > 100) {
      bodyString = this.CharacterFilter(bodyString)
    }
    return bodyString
  }
}

/**
 * 请求方法，对外暴露
 * @param config axios 配置
 * @param mock 是否使用 mock 数据
 * @returns Promise<any>
 */
const request = function (config, mock = false) {
  if (isAiGatewayUrl(config.url)) {
    // 新模型接口直接返回
    return new HttpRequest().request(config)
  }
  const API_PREFIX = '/pro/v1'
  // 非阿里云呼叫中心接口加上语言参数
  if (!config.aliCallCenter) {
    const localLang = utils.xbbLocalStorageGet('VUE_LANG') || 'zh_CN'
    config.url = API_PREFIX + config.url + `?lang=${localLang}`
  } else {
    config.url = API_PREFIX + config.url
  }

  let newConfig = config
  // 开发环境下处理 mock 和 dev 前缀
  if (WEBPACK_ENV === 'development') {
    if (mock) {
      newConfig = {
        ...config,
        url: '/mock' + config.url
      }
    } else {
      newConfig = {
        ...config,
        url: '/dev' + config.url
      }
    }
  }
  return new HttpRequest().request(newConfig)
}

export default request
